import React from "react";
import cn from "classnames";
import { styled } from "@linaria/react";
import { color, easingBounce, easingBounceMs, fonts, radius } from "..";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";
import { colors, spacings } from "../Theme";
import Text from "../Text";
import t from "../i18n";
import { ErrorWarningLine } from "../IconV2/react";
import replaceNewLines from "../lib/replaceNewLines";

interface P extends React.InputHTMLAttributes<HTMLTextAreaElement> {
  value?: string;
  minHeight?: number;
  children?: React.ReactNode;
  active?: boolean;
  animated?: boolean;
  maxLength?: number;
  enforceMaxLength?: boolean;
  showCharCounter?: boolean;
  error?: string | undefined;
  textareaClassName?: string;
  footerVisible?: boolean;
}

export const PADDING = 10.5;
const DEFAULT_MIN_HEIGHT = 41; // 1 lines
const MAX_HEIGHT_PX = 200;

const StyledTextarea = styled.textarea`
  display: block;
  resize: none;
  overflow-y: auto;
  color: ${color.carbon900};
  width: 100%;
  padding: ${spacings.xs} ${spacings.s};
  border: none;
  border-radius: ${radius};
  text-align: left;
  font: ${fonts.body1};
  text-overflow: clip;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  box-shadow: none !important;

  &[disabled] {
    background-color: ${colors.titanium50};
    opacity: 0.5;
  }

  &::placeholder {
    color: ${color.carbon500};
  }
`;

const Copy = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  margin: 1px;
  padding: ${PADDING}px;
  border: none;
  border-radius: ${radius};

  font: ${fonts.body1};
  text-align: left;
  overflow-y: hidden;
  text-overflow: clip;
  white-space: pre-wrap;
  overflow-wrap: break-word;

  visibility: hidden;
  z-index: -1;
`;

const ErrorWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 4px;
`;

const IconWrapper = styled.div`
  display: flex;
  margin: 1px 0px;
`;

const ErrorText = styled.p`
  color: ${colors.oxygen400};
  font: ${fonts.body2};
`;

const CounterText = styled(Text)`
  color: ${props => props.color ?? "inherit"};
  margin-top: 4px;
`;

const Border = styled.div<{ animated: boolean }>`
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  transition: ${({ animated }) =>
    animated ? `height ${easingBounceMs}ms ${easingBounce}` : "none"};
  border: 1px solid;
  border-color: ${color.titanium200};
  border-radius: ${radius};
  pointer-events: none;

  div:hover > &,
  textarea:hover {
    border-color: ${color.titanium300};
  }

  textarea:focus + & {
    border-color: ${color.lightNitrogen500_Main};
  }

  &.error,
  div:hover > &.error {
    border-color: ${colors.oxygen400};
  }

  &.disabled {
    border-color: ${colors.titanium200};
    pointer-events: none;
  }
`;

const Wrapper = styled.div`
  position: relative;

  &.disabled {
    pointer-events: none;
  }

  &:has(textarea:focus) ${Border} {
    border-color: ${color.lightNitrogen500_Main};
  }

  &:has(textarea:focus) ${Border}.error {
    border-color: ${colors.oxygen400};
  }
`;

const Footer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  pointer-events: auto;

  width: 100%;

  opacity: 0%;
  transition: opacity ${easingBounceMs}ms ${easingBounce};
  z-index: -1;
  &.visible {
    opacity: 100%;
    display: block;
    z-index: 1;
  }
`;

interface CharCounterProps {
  value: string;
  maxLength?: number;
}

const CharCounter = ({ value, maxLength }: CharCounterProps) => {
  const text = maxLength ? `${value.length}/${maxLength}` : `${value.length}`;
  const isError = maxLength && value.length > maxLength;

  return (
    <CounterText type="body2" color={isError ? colors.oxygen400 : color.carbon700}>
      {text} {t("textarea.maxLength.label")} {isError && t("textarea.maxLength.exceeded")}
    </CounterText>
  );
};

function TextareaInput(
  {
    className,
    textareaClassName,
    value = "",
    placeholder,
    onChange,
    minHeight = DEFAULT_MIN_HEIGHT,
    disabled,
    children,
    active,
    footerVisible,
    onFocus,
    onBlur,
    autoFocus,
    animated = true,
    maxLength,
    enforceMaxLength = true,
    showCharCounter,
    error,
  }: P,
  ref: React.Ref<HTMLTextAreaElement>,
) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const areaRef = useEnsuredForwardedRef(ref);
  const copyRef = React.useRef<HTMLDivElement>(null);
  const borderRef = React.useRef<HTMLDivElement>(null);
  const footerRef = React.useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = React.useState(active || false);
  const isFooterVisible = footerVisible ?? (isActive || value.length > 0);

  const behaviourCallback = React.useCallback(() => {
    const activeElement = window.document.activeElement; // get element with focus
    const area = areaRef.current;
    const wrapper = wrapperRef.current;
    const border = borderRef.current;
    const copy = copyRef.current;
    const footer = footerRef.current;

    if (wrapper && area && copy && border) {
      const offset = footer && children !== undefined ? footer.clientHeight : 0; // && footerVisible
      const textHeight = copy.clientHeight;

      // is active when textarea has focus or it was and mouse still hover the whole component
      // this is done in order to be able to interact with footer

      const newIsActive = active === undefined ? area === activeElement : active;
      const height = Math.min(MAX_HEIGHT_PX, Math.max(textHeight, minHeight));
      setIsActive(newIsActive);

      window.requestAnimationFrame(() => {
        area.style.height = `${height + offset}px`;
        border.style.height = `${height + offset}px`;
      });
    }
  }, [minHeight, areaRef, children, active]);

  React.useLayoutEffect(behaviourCallback, [behaviourCallback, value]);

  const handleFocus = React.useCallback(
    (e: React.FocusEvent<never>) => {
      onFocus?.(e);
      behaviourCallback();
    },
    [onFocus, behaviourCallback],
  );

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<never>) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.relatedTarget as Node | null)) {
        onBlur?.(e);
        behaviourCallback();
      }
    },
    [onBlur, behaviourCallback, wrapperRef],
  );

  React.useLayoutEffect(() => {
    if (!autoFocus) return;
    const ta = areaRef.current;
    if (!ta) throw new Error("Missing textarea");
    // tragedy: https://blog.maisie.ink/react-ref-autofocus/
    window.requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(ta.value.length, ta.value.length);
    });
  }, [areaRef, autoFocus]);

  const hasMaxlengthError = maxLength && value.length > maxLength;

  return (
    <Wrapper
      className={cn(className, { disabled })}
      ref={wrapperRef}
      onBlur={handleBlur}
      onFocus={handleFocus}
    >
      <StyledTextarea
        className={textareaClassName}
        ref={areaRef}
        onChange={onChange}
        onFocus={handleFocus}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={enforceMaxLength ? maxLength : undefined}
        spellCheck={false}
      />
      {error && !(showCharCounter && hasMaxlengthError) ? (
        <ErrorWrapper>
          <IconWrapper>
            <ErrorWarningLine color={colors.oxygen400} />
          </IconWrapper>
          <ErrorText>{error}</ErrorText>
        </ErrorWrapper>
      ) : null}
      {showCharCounter && <CharCounter value={value} maxLength={maxLength} />}
      <Border
        ref={borderRef}
        animated={animated}
        className={cn({ error: error || hasMaxlengthError, disabled })}
      >
        {children && (
          <Footer ref={footerRef} className={cn({ visible: isFooterVisible })}>
            {children}
          </Footer>
        )}
      </Border>
      <Copy ref={copyRef}>{replaceNewLines(value)}</Copy>
    </Wrapper>
  );
}

export default React.memo(React.forwardRef(TextareaInput));
